/**
 * Lookup Cache for Page ID ↔ Title Resolution
 * Provides high-performance bidirectional lookup between Wikipedia page IDs and titles
 */

import { CacheEntry, CacheStats } from '../types';
import { IdResolver } from '../IdResolver';

/**
 * Bidirectional lookup result
 */
export interface LookupResult {
  pageId?: number;
  title?: string;
  fromCache: boolean;
  timestamp: number;
}

/**
 * Cache configuration options
 */
export interface LookupCacheOptions {
  maxSize?: number;                  // Maximum number of entries (default: 2000)
  defaultTTL?: number;               // Default TTL in milliseconds (default: 1 hour)
  cleanupInterval?: number;          // Cleanup interval in milliseconds (default: 5 minutes)
  enableMetrics?: boolean;           // Whether to track performance metrics (default: true)
}

/**
 * High-performance bidirectional cache for Wikipedia page ID and title lookups
 * Maintains separate indexes for O(1) lookups in both directions
 */
export class LookupCache {
  // Primary storage
  private pageIdToTitle = new Map<number, CacheEntry<string>>();
  private titleToPageId = new Map<string, CacheEntry<number>>();
  
  // Performance tracking
  private stats = {
    hits: 0,
    misses: 0,
    sets: 0,
    deletes: 0,
    evictions: 0,
    cleanups: 0,
  };

  private options: Required<LookupCacheOptions>;
  private cleanupTimer?: NodeJS.Timeout;
  private lastCleanup = 0;

  constructor(options: LookupCacheOptions = {}) {
    this.options = {
      maxSize: options.maxSize || 2000,
      defaultTTL: options.defaultTTL || 3600000, // 1 hour
      cleanupInterval: options.cleanupInterval || 300000, // 5 minutes
      enableMetrics: options.enableMetrics !== false,
    };

    // Start periodic cleanup if enabled
    if (this.options.cleanupInterval > 0) {
      this.startCleanupTimer();
    }
  }

  /**
   * Get title by page ID
   */
  getTitleByPageId(pageId: number): string | null {
    const entry = this.pageIdToTitle.get(pageId);
    
    if (!entry || this.isExpired(entry)) {
      if (this.options.enableMetrics) this.stats.misses++;
      if (entry) {
        this.deleteByPageId(pageId);
      }
      return null;
    }

    // Update access tracking
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    
    if (this.options.enableMetrics) this.stats.hits++;
    return entry.data;
  }

  /**
   * Get page ID by title
   */
  getPageIdByTitle(title: string): number | null {
    const normalizedTitle = IdResolver.normalizeTitle(title);
    const entry = this.titleToPageId.get(normalizedTitle);
    
    if (!entry || this.isExpired(entry)) {
      if (this.options.enableMetrics) this.stats.misses++;
      if (entry) {
        this.deleteByTitle(normalizedTitle);
      }
      return null;
    }

    // Update access tracking
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    
    if (this.options.enableMetrics) this.stats.hits++;
    return entry.data;
  }

  /**
   * Get both page ID and title (whichever is available)
   */
  lookup(pageId?: number, title?: string): LookupResult {
    const now = Date.now();
    let result: LookupResult = {
      fromCache: false,
      timestamp: now,
    };

    // Look up by page ID first
    if (pageId !== undefined) {
      const cachedTitle = this.getTitleByPageId(pageId);
      if (cachedTitle) {
        result = {
          pageId,
          title: cachedTitle,
          fromCache: true,
          timestamp: now,
        };
      }
    }

    // If not found and title provided, look up by title
    if (!result.fromCache && title !== undefined) {
      const cachedPageId = this.getPageIdByTitle(title);
      if (cachedPageId) {
        result = {
          pageId: cachedPageId,
          title: IdResolver.normalizeTitle(title),
          fromCache: true,
          timestamp: now,
        };
      }
    }

    return result;
  }

  /**
   * Set a page ID ↔ title mapping
   */
  set(pageId: number, title: string, ttl?: number): void {
    if (!Number.isInteger(pageId) || pageId <= 0) {
      throw new Error(`Invalid page ID: ${pageId}`);
    }

    if (!IdResolver.isValidTitle(title)) {
      throw new Error(`Invalid title: ${title}`);
    }

    const normalizedTitle = IdResolver.normalizeTitle(title);
    const now = Date.now();
    const expires = now + (ttl || this.options.defaultTTL);

    // Check if we need to evict entries
    if (this.shouldEvict()) {
      this.evictLeastRecentlyUsed();
    }

    // Remove any existing mappings to maintain consistency
    this.deleteByPageId(pageId, false); // Don't update stats for internal cleanup
    this.deleteByTitle(normalizedTitle, false);

    // Create cache entries
    const titleEntry: CacheEntry<string> = {
      data: normalizedTitle,
      timestamp: now,
      expires,
      accessCount: 1,
      lastAccessed: now,
      size: this.calculateStringSize(normalizedTitle),
    };

    const pageIdEntry: CacheEntry<number> = {
      data: pageId,
      timestamp: now,
      expires,
      accessCount: 1,
      lastAccessed: now,
      size: 8, // Approximate size of number
    };

    // Store bidirectional mapping
    this.pageIdToTitle.set(pageId, titleEntry);
    this.titleToPageId.set(normalizedTitle, pageIdEntry);

    if (this.options.enableMetrics) this.stats.sets++;
  }

  /**
   * Delete mapping by page ID
   */
  deleteByPageId(pageId: number, updateStats = true): boolean {
    const titleEntry = this.pageIdToTitle.get(pageId);
    if (!titleEntry) return false;

    const title = titleEntry.data;
    
    this.pageIdToTitle.delete(pageId);
    this.titleToPageId.delete(title);
    
    if (updateStats && this.options.enableMetrics) {
      this.stats.deletes++;
    }
    
    return true;
  }

  /**
   * Delete mapping by title
   */
  deleteByTitle(title: string, updateStats = true): boolean {
    const normalizedTitle = IdResolver.normalizeTitle(title);
    const pageIdEntry = this.titleToPageId.get(normalizedTitle);
    if (!pageIdEntry) return false;

    const pageId = pageIdEntry.data;
    
    this.pageIdToTitle.delete(pageId);
    this.titleToPageId.delete(normalizedTitle);
    
    if (updateStats && this.options.enableMetrics) {
      this.stats.deletes++;
    }
    
    return true;
  }

  /**
   * Clear all cached mappings
   */
  clear(): void {
    const size = this.pageIdToTitle.size;
    this.pageIdToTitle.clear();
    this.titleToPageId.clear();
    
    if (this.options.enableMetrics) {
      this.stats.deletes += size;
    }
  }

  /**
   * Get cache performance statistics
   */
  getStats(): CacheStats & {
    evictions: number;
    cleanups: number;
    sets: number;
    deletes: number;
  } {
    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? this.stats.hits / totalRequests : 0;
    
    // Calculate memory usage
    let memoryUsage = 0;
    for (const [key, entry] of this.pageIdToTitle.entries()) {
      memoryUsage += 8 + (entry.size || 0); // pageId size + title size
    }
    
    return {
      size: this.pageIdToTitle.size,
      hits: this.stats.hits,
      misses: this.stats.misses,
      hitRate,
      memoryUsage,
      evictions: this.stats.evictions,
      cleanups: this.stats.cleanups,
      sets: this.stats.sets,
      deletes: this.stats.deletes,
    };
  }

  /**
   * Batch lookup for multiple items
   */
  batchLookup(items: Array<{ pageId?: number; title?: string }>): Map<string, LookupResult> {
    const results = new Map<string, LookupResult>();
    
    for (const item of items) {
      const key = item.pageId ? `page:${item.pageId}` : `title:${item.title}`;
      const result = this.lookup(item.pageId, item.title);
      results.set(key, result);
    }
    
    return results;
  }

  /**
   * Check if cache entry is expired
   */
  private isExpired(entry: CacheEntry): boolean {
    return entry.expires < Date.now();
  }

  /**
   * Check if we should evict entries due to size limit
   */
  private shouldEvict(): boolean {
    return this.pageIdToTitle.size >= this.options.maxSize;
  }

  /**
   * Evict least recently used entries
   */
  private evictLeastRecentlyUsed(): void {
    if (this.pageIdToTitle.size === 0) return;

    // Find entries to evict (oldest 10% or at least 1)
    const evictCount = Math.max(1, Math.floor(this.options.maxSize * 0.1));
    const entries = Array.from(this.pageIdToTitle.entries());
    
    // Sort by last accessed time (oldest first)
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    
    // Evict oldest entries
    for (let i = 0; i < evictCount && i < entries.length; i++) {
      const [pageId] = entries[i];
      this.deleteByPageId(pageId, false);
    }
    
    if (this.options.enableMetrics) {
      this.stats.evictions += evictCount;
    }
  }

  /**
   * Clean up expired entries
   */
  cleanupExpired(): number {
    const now = Date.now();
    let cleanedCount = 0;

    // Clean page ID → title mappings
    for (const [pageId, entry] of this.pageIdToTitle.entries()) {
      if (entry.expires < now) {
        this.deleteByPageId(pageId, false);
        cleanedCount++;
      }
    }

    if (this.options.enableMetrics && cleanedCount > 0) {
      this.stats.cleanups++;
    }

    this.lastCleanup = now;
    return cleanedCount;
  }

  /**
   * Start automatic cleanup timer
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanupExpired();
    }, this.options.cleanupInterval);

    // Don't keep the process alive for cleanup timer
    if (this.cleanupTimer.unref) {
      this.cleanupTimer.unref();
    }
  }

  /**
   * Stop automatic cleanup timer
   */
  stopCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
  }

  /**
   * Calculate approximate string size in bytes
   */
  private calculateStringSize(str: string): number {
    // Rough estimate: 2 bytes per character (UTF-16) + object overhead
    return str.length * 2 + 24;
  }

  /**
   * Get cache status summary
   */
  getStatusSummary(): {
    healthy: boolean;
    size: number;
    maxSize: number;
    hitRate: number;
    lastCleanup: number;
    issues: string[];
  } {
    const stats = this.getStats();
    const issues: string[] = [];
    
    if (stats.hitRate < 0.7) {
      issues.push(`Low hit rate: ${(stats.hitRate * 100).toFixed(1)}%`);
    }
    
    if (stats.size >= this.options.maxSize * 0.9) {
      issues.push(`Near capacity: ${stats.size}/${this.options.maxSize}`);
    }
    
    if (Date.now() - this.lastCleanup > this.options.cleanupInterval * 2) {
      issues.push('Cleanup overdue');
    }
    
    return {
      healthy: issues.length === 0,
      size: stats.size,
      maxSize: this.options.maxSize,
      hitRate: stats.hitRate,
      lastCleanup: this.lastCleanup,
      issues,
    };
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopCleanupTimer();
    this.clear();
  }
}